<div class="h-full flex flex-col justify-start items-center overflow-hidden">
    @if (!brand) {
    <p class="text-sm text-secondary px-2 py-4">Select a brand to edit profile.</p>
    } @else if (updatingBusinessInformation()) {
    <atom-loading-overlay></atom-loading-overlay>
    } @else {
    <div class="mx-auto h-full flex flex-col justify-start items-center overflow-y-auto w-full">
        @if (hasAssociatedAgencyId()) {
        <section
            class="mt-4 w-full rounded-lg border border-zinc-200 bg-white px-4 py-4 dark:border-zinc-700 dark:bg-zinc-800/50"
            aria-labelledby="associated-agency-heading">
            <div class="flex items-center justify-between gap-4">
                <h2 id="associated-agency-heading" class="m-0 text-sm font-semibold text-primary">Associated agency</h2>
                @if (associatedAgency()?.id) {
                <a [attr.href]="'/insights/agency/' + associatedAgency()?.id" (click)="viewAssociatedAgency($event)"
                    class="text-sm font-medium text-accent rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">View agency</a>
                }
            </div>

            @if (agencyLoadingState() === 'loading') {
            <div class="mt-3 flex items-center gap-2">
                <svg aria-hidden="true" class="size-5 text-gray-300 animate-spin dark:text-gray-600 fill-primary"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                </svg>
                <p class="m-0 text-sm text-secondary">Loading agency…</p>
            </div>
            } @else if (agencyLoadingState() === 'error') {
            <p class="m-0 mt-3 text-sm text-secondary">Unable to load agency details.</p>
            } @else if (associatedAgency()) {
            <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-6">
                <div class="sm:col-span-3">
                    <dt class="text-xs font-medium text-secondary">Name</dt>
                    <dd class="m-0 mt-1">
                        <p class="m-0 text-sm text-primary">{{ associatedAgency()?.name || '—' }}</p>
                    </dd>
                </div>
                <div class="sm:col-span-3">
                    <dt class="text-xs font-medium text-secondary">Website</dt>
                    <dd class="m-0 mt-1">
                        <p class="m-0 text-sm text-primary">{{ associatedAgency()?.website || '—' }}</p>
                    </dd>
                </div>
                <div class="sm:col-span-6">
                    <dt class="text-xs font-medium text-secondary">About</dt>
                    <dd class="m-0 mt-1">
                        <p class="m-0 text-sm text-primary">{{ associatedAgency()?.about || '—' }}</p>
                    </dd>
                </div>
            </dl>
            }
        </section>
        }

        <section
            class="mt-4 w-full rounded-lg border border-zinc-200 bg-white px-4 py-4 dark:border-zinc-700 dark:bg-zinc-800/50"
            aria-labelledby="application-review-window-heading">
            <h2 id="application-review-window-heading" class="m-0 text-sm font-semibold text-primary">Application review window</h2>
            <p class="m-0 mt-1 text-sm text-secondary">
                Applied creators are auto-declined after this many days if the brand has not hired or declined them.
                Changing this only applies to new applications. Existing Applied creators keep their original window.
            </p>
            <div class="mt-4">
                <label for="autoDeclineDays" class="block text-sm/6 font-medium text-primary">Days to review</label>
                <div class="mt-2 flex max-w-md items-center gap-3">
                    <input id="autoDeclineDays" type="number" min="1" max="90" inputmode="numeric"
                        placeholder="14" class="input-primary min-w-0 flex-1 text-primary"
                        [value]="autoDeclineDaysInput()"
                        (input)="onAutoDeclineDaysInput($event)" />
                    <button type="button" class="btn-shout btn-shout-sm btn-shout-primary shrink-0"
                        (click)="saveAutoDeclineDays()" [disabled]="savingAutoDeclineDays()">
                        {{ savingAutoDeclineDays() ? 'Saving…' : 'Save window' }}
                    </button>
                    <button type="button" class="btn-shout btn-shout-sm btn-shout-secondary shrink-0"
                        (click)="resetAutoDeclineDays()"
                        [disabled]="savingAutoDeclineDays() || !canResetAutoDeclineDays()">
                        Reset
                    </button>
                </div>
                <p class="m-0 mt-1 text-xs text-secondary">Reset turns auto-decline off for new applications. Allowed range is 1–90.</p>
            </div>
            @if (autoDeclineDaysError()) {
            <p class="m-0 mt-2 text-sm text-red-600" role="alert">{{ autoDeclineDaysError() }}</p>
            }
        </section>

        <form
            class="mx-auto flex flex-col justify-start items-center w-full divide-y divide-zinc-200 dark:divide-zinc-800">
            <div class="space-y-12 w-full">
                <!-- Brand Information section -->
            <div
                class="mt-6 grid grid-cols-1 gap-x-8 gap-y-10 border-b border-zinc-500/10 dark:border-zinc-800 pb-12 md:grid-cols-3">
                <!-- <div>
                    <h2 class="text-base/7 font-semibold text-primary">Brand Information</h2>
                    <p class="mt-1 text-sm/6 text-gray-400">A little bit about your brand and where Creators can find
                        you.</p>
                </div> -->

                <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-full w-full">


                    <div class="col-span-full">
                        <label for="photo" class="block text-sm/6 font-medium text-primary">Company Logo</label>
                        <div class="mt-2 flex items-center gap-x-3">
                            @if (companyLogo()) {
                            <img [src]="imageService.getImageSource(companyLogo(), 'gallery')" alt=""
                                class="size-16 shrink-0 rounded-full bg-gray-700 outline outline-1 -outline-offset-1 outline-white/10" />
                            } @else {
                            <svg viewBox="0 0 24 24" fill="currentColor" data-slot="icon" aria-hidden="true"
                                class="size-16 text-gray-500">
                                <path
                                    d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z"
                                    clip-rule="evenodd" fill-rule="evenodd" />
                            </svg>
                            }
                            <input #fileInput type="file" accept="image/*" class="hidden"
                                (change)="selectFile($event)" />
                            <button type="button" (click)="fileInput.click()"
                                class="text-primary secondary-button">Change</button>
                        </div>
                    </div>


                    <div class="sm:col-span-3">
                        <label for="first-name" class="block text-sm/6 font-medium text-primary">Company name</label>
                        <div class="mt-2">
                            <input [formField]="businessInformationForm.companyName" id="first-name" type="text"
                                autocomplete="given-name" class="input-primary text-primary" />
                        </div>
                    </div>
                    <div class="sm:col-span-3">
                        <label for="last-name" class="block text-sm/6 font-medium text-primary">Website</label>
                        <div class="mt-2 flex">
                            <div
                                class="flex shrink-0 items-center rounded-l-md bg-white/50 dark:bg-white/5 px-3 text-base text-gray-400 outline outline-1 -outline-offset-1 outline-zinc-300 dark:outline-white/10 sm:text-sm/6">
                                https://</div>
                            <input [formField]="businessInformationForm.website" id="company-website" type="text"
                                placeholder="www.example.com" class="input-url text-primary" autocomplete="website" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <label for="currencyCode" class="block text-sm/6 font-medium text-primary">Currency</label>
                        <el-select id="currencyCode" name="currencyCode" class="mt-2 block w-full"
                            [value]="businessInformationModel().currencyCode">
                            <button type="button" class="grid w-full grid-cols-1 text-primary dropdown-select-primary-btn">
                                <el-selectedcontent class="col-start-1 row-start-1 flex items-center gap-2 truncate pr-6">
                                    @if (selectedCurrencyOption(); as currency) {
                                    <img [src]="currency.flagUrl" [alt]="currency.label"
                                        class="h-4 w-6 shrink-0 rounded-sm object-cover" />
                                    <p class="m-0 truncate">{{ currency.label }}</p>
                                    } @else {
                                    <p class="m-0 truncate text-secondary">Select currency</p>
                                    }
                                </el-selectedcontent>
                                <svg viewBox="0 0 16 16" fill="currentColor" data-slot="icon" aria-hidden="true"
                                    class="col-start-1 row-start-1 size-5 self-center justify-self-end text-secondary sm:size-4">
                                    <path
                                        d="M5.22 10.22a.75.75 0 0 1 1.06 0L8 11.94l1.72-1.72a.75.75 0 1 1 1.06 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 0 1 0-1.06ZM10.78 5.78a.75.75 0 0 1-1.06 0L8 4.06 6.28 5.78a.75.75 0 0 1-1.06-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1 0 1.06Z"
                                        clip-rule="evenodd" fill-rule="evenodd" />
                                </svg>
                            </button>
                            <el-options anchor="bottom start" popover class="dropdown-select-primary-menu">
                                @for (currency of currencyOptions; track currency.value) {
                                <el-option [value]="currency.value" (click)="onCurrencySelected(currency.value)"
                                    class="group/option relative block cursor-pointer py-2 pr-9 pl-3 text-primary select-none focus:bg-primary focus:text-primary-foreground focus:outline-hidden">
                                    <div class="flex items-center gap-2">
                                        <img [src]="currency.flagUrl" [alt]="currency.label"
                                            class="h-4 w-6 shrink-0 rounded-sm object-cover" />
                                        <p class="m-0 truncate font-normal group-aria-selected/option:font-semibold">{{ currency.label }}
                                        </p>
                                    </div>
                                    <span
                                        class="absolute inset-y-0 right-0 flex items-center pr-4 text-accent group-not-aria-selected/option:hidden group-focus/option:text-primary-foreground in-[el-selectedcontent]:hidden">
                                        <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true" class="size-5">
                                            <path
                                                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                                clip-rule="evenodd" fill-rule="evenodd" />
                                        </svg>
                                    </span>
                                </el-option>
                                }
                            </el-options>
                        </el-select>
                    </div>

                    <div class="sm:col-span-6">
                        <label for="comment" class="block text-sm/6 font-medium text-primary">About</label>
                        <div class="mt-2">
                            <textarea [formField]="businessInformationForm.about" id="comment" rows="3"
                                class="input-textarea text-primary"></textarea>
                        </div>
                        <p class="mt-1 text-xs leading-6 text-gray-600">Write a few sentences
                            about your company.</p>
                    </div>

                    <div class="sm:col-span-3">
                        <label for="first-name" class="block text-sm/6 font-medium text-primary">Instagram</label>
                        <div class="mt-2 grid grid-cols-1">
                            <input [formField]="businessInformationForm.instagram" id="email" type="email"
                                placeholder="instagram handle" class="input-icon text-primary" />
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round"
                                class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-500 sm:size-4">
                                <circle cx="12" cy="12" r="4" />
                                <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
                            </svg>
                        </div>
                    </div>
                    <div class="sm:col-span-3">
                        <label for="last-name" class="block text-sm/6 font-medium text-primary">TikTok</label>
                        <div class="mt-2 grid grid-cols-1">
                            <input [formField]="businessInformationForm.tiktok" id="email" type="email"
                                placeholder="tiktok handle" class="input-icon text-primary" />
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round"
                                class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-500 sm:size-4">
                                <circle cx="12" cy="12" r="4" />
                                <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
                            </svg>
                        </div>
                    </div>



                </div>
            </div>
            <!-- Brand Information section END -->
        </div>
        <div class="mt-6 flex items-center justify-end gap-x-6 w-full">
            <button type="button" (click)="saveChanges()" class="btn-shout btn-shout-sm btn-shout-primary">Save</button>
        </div>
    </form>
    </div>
    }
</div>
